import axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { StorageService } from './storage.js'
import { attachErrorReporting, attachTracing } from './tracing.js';
import { configureErrorReporter, reportClientError } from './errorReporter.js';
import { getOrCreateDeviceId } from './deviceId.js';

/** Sin tope una petición colgada deja la pantalla esperando para siempre. */
const DEFAULT_TIMEOUT_MS = 60000;
const UPLOAD_TIMEOUT_MS = 300000;
const PDF_TIMEOUT_MS = 120000;
const REPORT_TIMEOUT_MS = 5000;

axios.defaults.baseURL = process.env.VUE_APP_API_PATH;
axios.defaults.headers.post['Content-Type'] = 'application/json';

// If token exists set header
let token = StorageService.getToken();
if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
}

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401 && !error.config?._sessionExpiredHandled && !error.config?._skipSessionExpired) {
            if (error.config) error.config._sessionExpiredHandled = true;
            StorageService.removeLoginData();
            window.dispatchEvent(new CustomEvent('erpz:session-expired'));
            window.setTimeout(() => window.location.assign(process.env.VUE_APP_PUBLIC_URL || '/'), 700);
        }
        return Promise.reject(error);
    },
);

let deviceId;
attachTracing(axios, {
    client: 'erpz-web',
    platform: 'web',
    timeoutMs: DEFAULT_TIMEOUT_MS,
    deviceId: () => {
        if (deviceId === undefined) {
            try { deviceId = getOrCreateDeviceId(window.localStorage); } catch { deviceId = null; }
        }
        return deviceId;
    },
});
attachErrorReporting(axios, reportClientError);
// Solo con sesión: sin token el reporte daría 401, y un 401 cierra la sesión.
configureErrorReporter((path, data) => (StorageService.getToken()
    ? axios.post(path, data, { timeout: REPORT_TIMEOUT_MS, _skipSessionExpired: true })
    : null));

export const ApiService = {
    init() {
        setupCache(axios, { interpretHeader: false });
    },
    removeHeader() {
        axios.defaults.headers.common = {}
    },
    get(apiPath, options = { cache:  false}) {
        return axios.get(apiPath, options)
    },
    async download(apiPath) {
        const base = process.env.VUE_APP_API_PATH;
        const token = StorageService.getToken();
        const response = await fetch(base + apiPath, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        return { data: blob };
    },
    post(apiPath, data) {
        return axios.post(apiPath, data);
    },

    postPDF(apiPath, data) {
        return axios.post(apiPath, data, {
            headers: {
                'Content-Type': 'application/pdf', // Indicamos que estamos enviando un PDF
                'Accept': '*/*', 
            },
            timeout: PDF_TIMEOUT_MS,
            responseType: 'blob'  // Si esperas que la respuesta también sea un archivo binario (PDF)
        });
    },
    
    postNoContentType(apiPath, data) {
        return axios.post(apiPath, data, {
            headers: {
                'Content-Type': undefined // Esto evitará que se establezca un Content-Type
            },
            timeout: UPLOAD_TIMEOUT_MS
        });
    },

    put(apiPath, data) {
        return axios.put(apiPath, data)
    },

    patch(apiPath, data) {
        return axios.patch(apiPath, data)
    },

    delete(apiPath) {
        return axios.delete(apiPath)
    },
    axios() {
        return axios;
    },
}
